import { useCallback, useEffect, useMemo, useState } from 'react';
import { isAcceptanceTest } from 'hub-http/helpers/env';
import { NetworkStatus } from '@apollo/client';
import { useGetAiAccessControls } from 'academy-common-lib/features/aiControl/aiAccessQueries';
import { VideoAssetTypename } from '../../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';
import { useAsyncBulkGenerateVideoSummaryMutation, useAsyncBulkGetVideoSummaryLazyQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/SummarizationRpc.hooks';
import { extractVideoMetadata } from '../utils/contentUtils';
import { useSummarySession } from '../utils/summaryUtils';
import { useLessonContentById } from '../../lessons/lessonQueries';
import { useFullShort } from '../../shortContent/shortContentQueries';
const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL = 3000;
export const useGenerateAsyncSummarization = ({
  primaryContentId,
  contentType,
  lessonContentId
}) => {
  var _pollData$tasks, _summaryResponse$vide, _summaryResponse$vide2, _cachedSummary$videoD;
  const [pollCount, setPollCount] = useState(0);
  const [shouldRetry, setShouldRetry] = useState(false);
  const [hasStartedPolling, setHasStartedPolling] = useState(false);
  const [timeoutError, setTimeoutError] = useState(false);
  const [summaryLoadingState, setSummaryLoadingState] = useState(false);
  const [currentContentId, setCurrentContentId] = useState(null);
  const isShorts = contentType === 'SHORT_FORM_CONTENT';
  const contentId = lessonContentId || primaryContentId;
  const {
    getSummaryState,
    setSummaryTaskId,
    setSummaryData
  } = useSummarySession();
  const cachedSummary = getSummaryState(contentId, contentType);
  const {
    short
  } = useFullShort(primaryContentId, !isShorts);
  const lessonContent = useLessonContentById({
    lessonId: primaryContentId,
    contentId: lessonContentId,
    skip: isShorts || !lessonContentId || !primaryContentId
  });
  const videoContent = (lessonContent === null || lessonContent === void 0 ? void 0 : lessonContent.__typename) === VideoAssetTypename ? lessonContent : undefined;
  const content = isShorts ? short : videoContent;
  const {
    contentPortalId,
    outputLanguage,
    videoId
  } = extractVideoMetadata(content);
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const aiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const [generateTaskId, {
    data: generateData,
    loading: generateLoading,
    error: generateError,
    reset: resetMutation
  }] = useAsyncBulkGenerateVideoSummaryMutation({
    onCompleted: response => {
      var _response$result;
      const task = response === null || response === void 0 || (_response$result = response.result) === null || _response$result === void 0 || (_response$result = _response$result.tasks) === null || _response$result === void 0 ? void 0 : _response$result[0];
      if (task !== null && task !== void 0 && task.taskId) {
        setSummaryTaskId({
          contentType,
          contentId,
          taskId: task.taskId
        });
        setPollCount(0);
      }
    }
  });
  const [pollForSummary, {
    data: pollData,
    loading: pollLoading,
    error: pollError,
    networkStatus: pollNetworkStatus,
    stopPolling
  }] = useAsyncBulkGetVideoSummaryLazyQuery({
    pollInterval: POLL_INTERVAL,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  });

  // Reset state when navigating between videos
  useEffect(() => {
    if (currentContentId !== null && currentContentId !== contentId) {
      stopPolling();
      setHasStartedPolling(false);
      setPollCount(0);
      resetMutation();
    }
    setCurrentContentId(contentId);
  }, [contentId, currentContentId, stopPolling, resetMutation]);

  // Only use poll data if it matches the current video (prevents stale Apollo cache data)
  const summaryResponse = pollData === null || pollData === void 0 || (_pollData$tasks = pollData.tasks) === null || _pollData$tasks === void 0 ? void 0 : _pollData$tasks[0];
  const shouldUsePollData = hasStartedPolling && !(cachedSummary !== null && cachedSummary !== void 0 && cachedSummary.summary) && currentContentId === contentId;
  const summary = shouldUsePollData ? summaryResponse === null || summaryResponse === void 0 || (_summaryResponse$vide = summaryResponse.videoSummarizationResponse) === null || _summaryResponse$vide === void 0 ? void 0 : _summaryResponse$vide.summary : undefined;
  const correlationId = shouldUsePollData ? summaryResponse === null || summaryResponse === void 0 || (_summaryResponse$vide2 = summaryResponse.videoSummarizationResponse) === null || _summaryResponse$vide2 === void 0 ? void 0 : _summaryResponse$vide2.correlationId : undefined;
  const status = shouldUsePollData ? summaryResponse === null || summaryResponse === void 0 ? void 0 : summaryResponse.status : undefined;
  const summaryReady = status === 'COMPLETED';
  const retryGeneration = !!pollError || status === 'NOT_FOUND' || status === 'FAILED';
  const isPolling = pollNetworkStatus === NetworkStatus.poll || pollLoading;

  // Store summary in Zustand for persistence across video navigation
  useEffect(() => {
    if (summary && !(cachedSummary !== null && cachedSummary !== void 0 && cachedSummary.summary)) {
      setSummaryData({
        contentType,
        contentId,
        summary,
        correlationId,
        videoDuration: content === null || content === void 0 ? void 0 : content.duration
      });
    }
  }, [summary, cachedSummary, setSummaryData, contentType, contentId, correlationId, content === null || content === void 0 ? void 0 : content.duration]);

  // Track polling progress and stop when complete/failed/timeout
  useEffect(() => {
    if (!summaryReady && isPolling) {
      setPollCount(prev => prev + 1);
      setSummaryLoadingState(true);
    }
    if (summaryReady || retryGeneration || pollCount > MAX_POLL_ATTEMPTS) {
      stopPolling();
      setPollCount(0);
      setSummaryLoadingState(false);
      setTimeoutError(pollCount > MAX_POLL_ATTEMPTS);
    }
    // Intentionally excluding pollCount from deps to avoid re-triggering effect on every poll increment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling, pollData, pollError, isPolling, summaryReady, retryGeneration]);

  // Build variables for summary generation RPC
  const summarizationVariables = useMemo(() => {
    if (!contentPortalId || !videoId) {
      return undefined;
    }
    return {
      videos: [Object.assign({
        videoId,
        contentPortalId
      }, outputLanguage && typeof outputLanguage === 'string' && {
        outputLanguage
      })]
    };
  }, [contentPortalId, outputLanguage, videoId]);
  const shouldGenerate = useMemo(() => aiEnabled && !isAcceptanceTest() && !!summarizationVariables && !(cachedSummary !== null && cachedSummary !== void 0 && cachedSummary.taskId) && !(cachedSummary !== null && cachedSummary !== void 0 && cachedSummary.summary) && !generateLoading && !generateData && !generateError, [aiEnabled, summarizationVariables, cachedSummary === null || cachedSummary === void 0 ? void 0 : cachedSummary.taskId, cachedSummary === null || cachedSummary === void 0 ? void 0 : cachedSummary.summary, generateLoading, generateData, generateError]);

  // Trigger generation if not already generated and conditions are met
  useEffect(() => {
    if ((shouldGenerate || shouldRetry) && summarizationVariables) {
      generateTaskId({
        variables: summarizationVariables
      }).catch(() => {});
      setShouldRetry(false);
    }
  }, [shouldGenerate, shouldRetry, generateTaskId, summarizationVariables]);

  // Start polling once we have a taskId from generation
  useEffect(() => {
    if (cachedSummary !== null && cachedSummary !== void 0 && cachedSummary.taskId && !(cachedSummary !== null && cachedSummary !== void 0 && cachedSummary.summary) && !hasStartedPolling) {
      pollForSummary({
        variables: {
          taskIds: [cachedSummary.taskId]
        }
      });
      setHasStartedPolling(true);
    }
  }, [cachedSummary === null || cachedSummary === void 0 ? void 0 : cachedSummary.taskId, cachedSummary === null || cachedSummary === void 0 ? void 0 : cachedSummary.summary, hasStartedPolling, pollForSummary]);
  const refetchSummary = useCallback(() => {
    resetMutation();
    setSummaryTaskId({
      contentType,
      contentId,
      taskId: undefined
    });
    setPollCount(0);
    setHasStartedPolling(false);
    setTimeoutError(false);
    setShouldRetry(true);
  }, [resetMutation, setSummaryTaskId, contentType, contentId]);
  const apolloError = generateError || pollError;
  const summaryError = !!apolloError || timeoutError || retryGeneration;
  return {
    summary: (cachedSummary === null || cachedSummary === void 0 ? void 0 : cachedSummary.summary) || summary,
    correlationId: (cachedSummary === null || cachedSummary === void 0 ? void 0 : cachedSummary.correlationId) || correlationId,
    summaryError,
    summaryLoading: generateLoading || summaryLoadingState,
    refetchSummary,
    videoDuration: (_cachedSummary$videoD = cachedSummary === null || cachedSummary === void 0 ? void 0 : cachedSummary.videoDuration) !== null && _cachedSummary$videoD !== void 0 ? _cachedSummary$videoD : content === null || content === void 0 ? void 0 : content.duration
  };
};